'use client';

import React, { useState, useRef } from 'react';
import {
  Scan, MapPin, CheckCircle2, AlertTriangle, Package, Layers,
  ArrowRight, RotateCcw, Hash, Calendar, ChevronRight, Info,
  Zap, Archive, Star, Shuffle
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type SlotPriority = 'same-batch' | 'empty-rack' | 'fast-moving' | 'overflow';

interface SlottingItem {
  id: string;
  barcode: string;
  itemName: string;
  batchNumber: string;
  expiryDate: string;
  quantity: string;
  uom: string;
  grnId: string;
  shelfLifePct: number | null;
  status: 'pending' | 'slotted' | 'skipped';
  suggestedRack?: string;
  confirmedRack?: string;
  priority?: SlotPriority;
}

interface RackSuggestion {
  rack: string;
  priority: SlotPriority;
  reason: string;
  zone: string;
  capacity: string;
}

// ─── Mock approved-QC items awaiting putaway ──────────────────────────────────

const APPROVED_ITEMS: SlottingItem[] = [
  {
    id: 'INV-12345601',
    barcode: 'ITM001|BAT-2026-001|31122027|00001',
    itemName: 'Non Woven Tape 75mm x 250cm',
    batchNumber: 'BAT-2026-001',
    expiryDate: '2027-06-01',
    quantity: '100',
    uom: 'ROLL',
    grnId: 'GRN-20260612-001',
    shelfLifePct: 82,
    status: 'pending',
  },
  {
    id: 'INV-12345603',
    barcode: 'ITM003|BAT-2026-003|01092028|00003',
    itemName: 'Mayo S Trolley Cover Small',
    batchNumber: 'BAT-2026-003',
    expiryDate: '2028-09-01',
    quantity: '200',
    uom: 'PCS',
    grnId: 'GRN-20260612-001',
    shelfLifePct: 91,
    status: 'pending',
  },
  {
    id: 'INV-12345604',
    barcode: 'ITM004|AUTO-345604|NOEXP|00004',
    itemName: 'BP Scalpel Handle Size 4',
    batchNumber: 'AUTO-345604',
    expiryDate: 'No Expiry',
    quantity: '20',
    uom: 'PCS',
    grnId: 'GRN-20260612-002',
    shelfLifePct: null,
    status: 'pending',
  },
  {
    id: 'INV-12345606',
    barcode: 'ITM006|BAT-2026-006|15032027|00006',
    itemName: 'Surgical Gloves Size M',
    batchNumber: 'BAT-2026-006',
    expiryDate: '2027-03-15',
    quantity: '500',
    uom: 'PCS',
    grnId: 'GRN-20260612-002',
    shelfLifePct: 75,
    status: 'pending',
  },
  {
    id: 'INV-12345607',
    barcode: 'ITM007|BAT-2026-007|30062026|00007',
    itemName: 'Gauze Swab 10x10cm Sterile',
    batchNumber: 'BAT-2026-007',
    expiryDate: '2026-06-30',
    quantity: '300',
    uom: 'PCS',
    grnId: 'GRN-20260611-087',
    shelfLifePct: 42,
    status: 'pending',
  },
];

// ─── Rule engine: suggest rack based on priority ──────────────────────────────

function suggestRack(item: SlottingItem): RackSuggestion {
  // Priority 1: Same batch already exists in a location
  if (item.batchNumber === 'BAT-2026-001') {
    return {
      rack: 'A-01-02-B-05',
      priority: 'same-batch',
      reason: 'Existing batch BAT-2026-001 already stored here',
      zone: 'Zone A — Aisle 01',
      capacity: '40% used',
    };
  }
  // Priority 2: Empty rack available
  if (item.shelfLifePct !== null && item.shelfLifePct >= 80) {
    return {
      rack: 'B-02-03-A-01',
      priority: 'empty-rack',
      reason: 'Empty slot available in standard storage zone',
      zone: 'Zone B — Aisle 02',
      capacity: '0% used',
    };
  }
  // Priority 3: Fast-moving zone (high turnover items)
  if (item.quantity && parseInt(item.quantity) >= 200) {
    return {
      rack: 'A-01-01-A-03',
      priority: 'fast-moving',
      reason: 'High-volume item — assigned to fast-moving zone',
      zone: 'Zone A — Aisle 01 (Fast)',
      capacity: '55% used',
    };
  }
  // Priority 4: Overflow zone
  return {
    rack: 'D-04-01-C-02',
    priority: 'overflow',
    reason: 'No preferred slot available — overflow zone assigned',
    zone: 'Zone D — Overflow',
    capacity: '70% used',
  };
}

// ─── Priority badge config ────────────────────────────────────────────────────

const PRIORITY_CONFIG: Record<SlotPriority, { label: string; color: string; bg: string; border: string; Icon: React.ComponentType<{ size?: number; className?: string }> }> = {
  'same-batch': {
    label: 'Same Batch',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/30',
    Icon: Star,
  },
  'empty-rack': {
    label: 'Empty Rack',
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    border: 'border-emerald-400/30',
    Icon: Archive,
  },
  'fast-moving': {
    label: 'Fast-Moving Zone',
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/30',
    Icon: Zap,
  },
  overflow: {
    label: 'Overflow Zone',
    color: 'text-muted-foreground',
    bg: 'bg-muted/50',
    border: 'border-border',
    Icon: Shuffle,
  },
};

// ─── Shelf life bar ───────────────────────────────────────────────────────────

function ShelfLifeBar({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-2xs text-muted-foreground">No Expiry</span>;
  const color = pct >= 80 ? 'bg-primary' : pct >= 50 ? 'bg-accent' : 'bg-red-400';
  const textColor = pct >= 80 ? 'text-primary' : pct >= 50 ? 'text-accent' : 'text-red-400';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden min-w-[60px]">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-2xs font-bold tabular-nums ${textColor}`}>{pct}%</span>
    </div>
  );
}

// ─── Main board ───────────────────────────────────────────────────────────────

export default function RackSlottingBoard() {
  const [items, setItems] = useState<SlottingItem[]>(APPROVED_ITEMS);
  const [scanInput, setScanInput] = useState('');
  const [activeItem, setActiveItem] = useState<SlottingItem | null>(null);
  const [suggestion, setSuggestion] = useState<RackSuggestion | null>(null);
  const [rackScanInput, setRackScanInput] = useState('');
  const [confirmState, setConfirmState] = useState<'idle' | 'confirming' | 'confirmed' | 'mismatch'>('idle');
  const [flashType, setFlashType] = useState<'success' | 'error' | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const rackRef = useRef<HTMLInputElement>(null);

  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const slottedCount = items.filter((i) => i.status === 'slotted').length;

  // Handle item barcode scan
  const handleItemScan = (e: React.FormEvent) => {
    e.preventDefault();
    const val = scanInput.trim();
    if (!val) return;

    const found = items.find(
      (i) => i.barcode === val || i.id === val || i.batchNumber === val || i.barcode.startsWith(val.split('|')[0])
    );

    if (!found) {
      setFlashType('error');
      setTimeout(() => setFlashType(null), 1200);
      setScanInput('');
      return;
    }

    if (found.status === 'slotted') {
      setScanInput('');
      return;
    }

    const rack = suggestRack(found);
    setActiveItem({ ...found, suggestedRack: rack.rack, priority: rack.priority });
    setSuggestion(rack);
    setConfirmState('confirming');
    setRackScanInput('');
    setScanInput('');
    setTimeout(() => rackRef.current?.focus(), 100);
  };

  // Handle rack barcode scan / confirm
  const handleRackConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeItem || !suggestion) return;
    const val = rackScanInput.trim();

    if (!val) return;

    if (val === suggestion.rack) {
      // Confirmed
      setItems((prev) =>
        prev.map((i) =>
          i.id === activeItem.id
            ? { ...i, status: 'slotted', confirmedRack: val, suggestedRack: suggestion.rack, priority: suggestion.priority }
            : i
        )
      );
      setConfirmState('confirmed');
      setFlashType('success');
      setTimeout(() => {
        setFlashType(null);
        setActiveItem(null);
        setSuggestion(null);
        setConfirmState('idle');
        setRackScanInput('');
        scanRef.current?.focus();
      }, 1800);
    } else {
      setConfirmState('mismatch');
      setFlashType('error');
      setTimeout(() => {
        setFlashType(null);
        setConfirmState('confirming');
        setRackScanInput('');
        rackRef.current?.focus();
      }, 1500);
    }
  };

  const handleSkip = () => {
    if (!activeItem) return;
    setItems((prev) =>
      prev.map((i) => (i.id === activeItem.id ? { ...i, status: 'skipped' } : i))
    );
    setActiveItem(null);
    setSuggestion(null);
    setConfirmState('idle');
    setRackScanInput('');
    setTimeout(() => scanRef.current?.focus(), 100);
  };

  const handleReset = (id: string) => {
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, status: 'pending', confirmedRack: undefined, suggestedRack: undefined, priority: undefined } : i))
    );
  };

  // Quick-select item from list (tap on tablet)
  const handleSelectItem = (item: SlottingItem) => {
    if (item.status !== 'pending') return;
    const rack = suggestRack(item);
    setActiveItem({ ...item, suggestedRack: rack.rack, priority: rack.priority });
    setSuggestion(rack);
    setConfirmState('confirming');
    setRackScanInput('');
    setTimeout(() => rackRef.current?.focus(), 100);
  };

  return (
    <div className="flex-1 flex flex-col lg:flex-row gap-0 relative overflow-hidden">

      {/* Flash overlay */}
      {flashType && (
        <div
          className={`fixed inset-0 z-50 pointer-events-none transition-opacity duration-300 ${
            flashType === 'success' ? 'bg-primary/20' : 'bg-red-500/20'
          }`}
        />
      )}

      {/* ── Left panel: item queue ── */}
      <div className="w-full lg:w-80 xl:w-96 border-r border-border bg-card flex flex-col flex-shrink-0">
        {/* Stats */}
        <div className="px-4 py-3 border-b border-border grid grid-cols-3 gap-2">
          <div className="bg-muted/40 rounded-lg px-2 py-2 text-center">
            <p className="text-lg font-bold text-accent tabular-nums">{pendingCount}</p>
            <p className="text-2xs text-muted-foreground">Pending</p>
          </div>
          <div className="bg-muted/40 rounded-lg px-2 py-2 text-center">
            <p className="text-lg font-bold text-primary tabular-nums">{slottedCount}</p>
            <p className="text-2xs text-muted-foreground">Slotted</p>
          </div>
          <div className="bg-muted/40 rounded-lg px-2 py-2 text-center">
            <p className="text-lg font-bold text-foreground tabular-nums">{items.length}</p>
            <p className="text-2xs text-muted-foreground">Total</p>
          </div>
        </div>

        {/* Item scan input */}
        <div className="px-4 py-3 border-b border-border">
          <form onSubmit={handleItemScan} className="flex gap-2">
            <div className="flex-1 relative">
              <Scan size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={scanRef}
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                placeholder="Scan item barcode…"
                className="w-full bg-muted/40 border border-border rounded-lg pl-8 pr-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all font-mono"
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="px-3 py-2.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 transition-all flex items-center gap-1"
            >
              <ArrowRight size={14} />
            </button>
          </form>
          <p className="text-2xs text-muted-foreground mt-1.5 px-1">Or tap any pending item below</p>
        </div>

        {/* Item list */}
        <div className="flex-1 overflow-y-auto py-2 space-y-1 px-2">
          {items.map((item) => {
            const isActive = activeItem?.id === item.id;
            const cfg = item.priority ? PRIORITY_CONFIG[item.priority] : null;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSelectItem(item)}
                disabled={item.status !== 'pending'}
                className={`w-full text-left px-3 py-3 rounded-xl border transition-all ${
                  isActive
                    ? 'border-primary bg-primary/8 ring-1 ring-primary/30'
                    : item.status === 'slotted' ?'border-primary/20 bg-primary/3 opacity-70'
                    : item.status === 'skipped' ?'border-border bg-muted/20 opacity-50' :'border-border bg-card hover:bg-muted/30 hover:border-primary/30 cursor-pointer'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-foreground leading-tight truncate">{item.itemName}</p>
                    <p className="text-2xs font-mono text-primary mt-0.5">{item.batchNumber}</p>
                    <p className="text-2xs text-muted-foreground mt-0.5">{item.grnId}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {item.status === 'slotted' && (
                      <span className="badge-available text-2xs font-bold px-1.5 py-0.5 rounded-full">SLOTTED</span>
                    )}
                    {item.status === 'skipped' && (
                      <span className="text-2xs font-bold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">SKIPPED</span>
                    )}
                    {item.status === 'pending' && (
                      <span className="badge-pending text-2xs font-bold px-1.5 py-0.5 rounded-full">PENDING</span>
                    )}
                    {item.status === 'slotted' && item.confirmedRack && (
                      <span className="text-2xs font-mono text-primary">{item.confirmedRack}</span>
                    )}
                  </div>
                </div>
                {item.status === 'slotted' && cfg && (
                  <div className={`mt-1.5 flex items-center gap-1.5 px-2 py-1 rounded-md ${cfg.bg} border ${cfg.border}`}>
                    <cfg.Icon size={10} className={cfg.color} />
                    <span className={`text-2xs font-semibold ${cfg.color}`}>{cfg.label}</span>
                  </div>
                )}
                {item.status === 'slotted' && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleReset(item.id); }}
                    className="mt-1.5 text-2xs text-muted-foreground hover:text-foreground underline transition-colors"
                  >
                    Reset
                  </button>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Right panel: suggestion + confirm ── */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 bg-background">
        {confirmState === 'idle' && (
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 rounded-2xl bg-muted/40 flex items-center justify-center mx-auto mb-5">
              <Scan size={36} className="text-muted-foreground/40" />
            </div>
            <h2 className="text-lg font-bold text-foreground mb-2">Scan Item Barcode</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Scan or enter an item barcode on the left panel. The system will suggest the optimal rack location based on priority rules.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3 text-left">
              {(Object.entries(PRIORITY_CONFIG) as [SlotPriority, typeof PRIORITY_CONFIG[SlotPriority]][]).map(([key, cfg]) => (
                <div key={key} className={`flex items-start gap-2.5 px-3 py-2.5 rounded-xl border ${cfg.border} ${cfg.bg}`}>
                  <cfg.Icon size={14} className={`${cfg.color} flex-shrink-0 mt-0.5`} />
                  <div>
                    <p className={`text-xs font-bold ${cfg.color}`}>{cfg.label}</p>
                    <p className="text-2xs text-muted-foreground mt-0.5">
                      {key === 'same-batch' && 'Consolidate with existing batch'}
                      {key === 'empty-rack' && 'Assign to free slot'}
                      {key === 'fast-moving' && 'High-turnover zone'}
                      {key === 'overflow' && 'Fallback when no slot'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(confirmState === 'confirming' || confirmState === 'mismatch') && activeItem && suggestion && (
          <div className="w-full max-w-lg space-y-4">
            {/* Item info card */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-muted/50 flex items-center justify-center flex-shrink-0">
                  <Package size={20} className="text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground leading-tight">{activeItem.itemName}</p>
                  <p className="text-2xs font-mono text-primary mt-0.5">{activeItem.barcode}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Hash size={10} /> Batch</p>
                  <p className="text-xs font-mono font-bold text-primary">{activeItem.batchNumber}</p>
                </div>
                <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Calendar size={10} /> Expiry</p>
                  <p className="text-xs font-semibold text-foreground">{activeItem.expiryDate}</p>
                </div>
                <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Qty</p>
                  <p className="text-xs font-bold text-foreground">{activeItem.quantity} <span className="text-muted-foreground font-normal">{activeItem.uom}</span></p>
                </div>
                <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                  <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">Shelf Life</p>
                  <ShelfLifeBar pct={activeItem.shelfLifePct} />
                </div>
              </div>
            </div>

            {/* Suggested rack card */}
            {(() => {
              const cfg = PRIORITY_CONFIG[suggestion.priority];
              return (
                <div className={`bg-card border-2 rounded-2xl p-5 ${cfg.border}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <cfg.Icon size={15} className={cfg.color} />
                    <span className={`text-xs font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label} — Priority {['same-batch','empty-rack','fast-moving','overflow'].indexOf(suggestion.priority) + 1}</span>
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1">
                      <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><MapPin size={10} /> Suggested Rack</p>
                      <p className="text-3xl font-black font-mono text-foreground tracking-wider">{suggestion.rack}</p>
                    </div>
                    <ChevronRight size={20} className="text-muted-foreground/40" />
                    <div className="text-right">
                      <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1 justify-end"><Layers size={10} /> Zone</p>
                      <p className="text-xs font-semibold text-foreground">{suggestion.zone}</p>
                      <p className="text-2xs text-muted-foreground mt-0.5">{suggestion.capacity}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-muted/30">
                    <Info size={12} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                    <p className="text-2xs text-muted-foreground">{suggestion.reason}</p>
                  </div>
                </div>
              );
            })()}

            {/* Rack scan confirm */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <p className="text-sm font-bold text-foreground mb-1">Confirm Rack Location</p>
              <p className="text-2xs text-muted-foreground mb-3">Scan the rack barcode at <span className="font-mono font-bold text-foreground">{suggestion.rack}</span> to confirm putaway</p>
              <form onSubmit={handleRackConfirm} className="flex gap-2">
                <div className="flex-1 relative">
                  <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    ref={rackRef}
                    value={rackScanInput}
                    onChange={(e) => setRackScanInput(e.target.value)}
                    placeholder={`Scan rack barcode (${suggestion.rack})…`}
                    className={`w-full bg-muted/40 border rounded-xl pl-8 pr-3 py-3 text-sm font-mono text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 transition-all ${
                      confirmState === 'mismatch' ?'border-red-500 ring-red-500/30 bg-red-500/5' :'border-border focus:ring-primary focus:border-primary'
                    }`}
                  />
                </div>
                <button
                  type="submit"
                  className="px-4 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-all flex items-center gap-2"
                >
                  <CheckCircle2 size={16} />
                  Confirm
                </button>
              </form>
              {confirmState === 'mismatch' && (
                <div className="mt-2.5 flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                  <AlertTriangle size={13} className="text-red-400 flex-shrink-0" />
                  <p className="text-xs text-red-400 font-semibold">Rack mismatch — scan the correct rack: <span className="font-mono">{suggestion.rack}</span></p>
                </div>
              )}
              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="text-xs text-muted-foreground hover:text-foreground underline transition-colors"
                >
                  Skip this item
                </button>
                <button
                  type="button"
                  onClick={() => { setActiveItem(null); setSuggestion(null); setConfirmState('idle'); setRackScanInput(''); }}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <RotateCcw size={12} />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmState === 'confirmed' && activeItem && suggestion && (
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={40} className="text-primary" />
            </div>
            <h2 className="text-xl font-black text-primary mb-2">Putaway Confirmed!</h2>
            <p className="text-sm text-foreground font-semibold mb-1">{activeItem.itemName}</p>
            <p className="text-2xl font-black font-mono text-foreground mb-1">{suggestion.rack}</p>
            <p className="text-xs text-muted-foreground">{suggestion.zone}</p>
          </div>
        )}
      </div>
    </div>
  );
}
